/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {PolygonUtilService} from '../../../../services/features/polygon-util.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {MapToolsService} from '../../../../services/map-tools.service';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {PlanModelService} from '../../../../../common/service/plan-model.service';
import {FeaturesApplyInsertService} from '../../../../services/features-apply-insert.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {AppConfigService} from '../../../../../common/service/app-config.service';
import {ToolServiceInterface} from './tool-service-interface';
import {first} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PolygonConstructionToolService implements ToolServiceInterface{
    title = 'PolygonConstructionToolService';

    attributes: any;

    constructor(
        private appConfigService: AppConfigService,
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private mapToolsService: MapToolsService,
        private planModelService: PlanModelService,
        private polygonUtilService: PolygonUtilService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService
    ) {}

    create(attributes: any) {
        console.debug(`${this.title} - create`);

        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_POLYGON', () => {
            this.attributes = attributes;

            const tool = this.getTool();

            tool.errors.length = 0;
            tool.warnings.length = 0;
            tool.information.length = 0;

            const selectedLines = this.verifyLineTypes();
            if (selectedLines) {
                this.createPolygon(selectedLines);
            }
        });
    }

    private getTool() {
        return this.constructionToolModelService.getTool('polygon');
    }

    private createPolygon(selectedLines: any[]) {
        console.debug(`${this.title} - createPolygon`);

        const polygonRingInfo = this.polygonUtilService.getPolygonRing(selectedLines, undefined);
        let ring = polygonRingInfo.ring;
        let lineIdsString = polygonRingInfo.lineids;

        if (ring) {
            const polygonGeometry = new this.esriModuleService.geometry.Polygon();
            polygonGeometry.setSpatialReference(ring[0].spatialReference);

            if (polygonGeometry.isClockwise(ring)) {
                polygonGeometry.addRing(ring);
            } else {
                ring = ring.reverse();
                lineIdsString = lineIdsString.split(',').reverse().join(',');
                polygonGeometry.addRing(ring);
            }

            const polygonAttributes = {id: 0, lineids: lineIdsString};

            for (const [key, value] of Object.entries(this.attributes) as any[]) {
                if (value && typeof value === 'object') {
                    polygonAttributes[key] = value.id;
                } else {
                    polygonAttributes[key] = value;
                }
            }

            polygonAttributes.id = this.spatialSequenceService.getPolygonSequence(1)[0];

            const polygonGraphic = new this.esriModuleService.Graphic(polygonGeometry);
            polygonGraphic.attributes = polygonAttributes;

            // derive area if not set
            const projectedGeometry = this.spatialUtilService.project(polygonGraphic.geometry, this.planModelService.getSpatialReference());

            const areasAndLengthParams = new this.esriModuleService.AreasAndLengthsParameters();
            areasAndLengthParams.lengthUnit = this.esriModuleService.GeometryService.UNIT_METER;
            areasAndLengthParams.areaUnit = this.esriModuleService.GeometryService.UNIT_SQUARE_METERS;
            areasAndLengthParams.calculationType = 'geodesic';

            areasAndLengthParams.polygons = [projectedGeometry];

            const geometryService = new this.esriModuleService.GeometryService(this.appConfigService.getDesignProperties().GEOMETRY_SERVER_URL);
            geometryService.areasAndLengths(areasAndLengthParams, (result: any) => {
                if (!polygonGraphic.attributes['statedarea']) {
                    polygonGraphic.attributes['statedarea'] = Math.round(result.areas[0]) + '';
                }
                console.debug('Polygon statedarea = ' + polygonGraphic.attributes['statedarea'] + ' derived area = ' + Math.round(result.areas[0]));

                this.featuresApplyInsertService
                    .insertFeaturesByType(this.spatialUtilService.getPolygonMapLayer(), [polygonGraphic])
                    .pipe(first())
                    .subscribe((createdPolygons: any[]) => {
                        if (createdPolygons && createdPolygons.length > 0) {
                            console.debug('Polygon has been successfully created!');
                        } else {
                            console.debug('Error creating polygon!');
                            this.getTool().errors.push('Error occurred creating polygon feature.');
                        }
                    });
            });
        } else {
            console.debug('Polygon cannot be formed with current selected lines!');
            const tool = this.getTool();
            tool.errors.push(tool.errorMessage3);
        }
    }

    private verifyLineTypes() {
        console.debug(`${this.title} - verifyLineTypes`);

        const tool = this.getTool();

        const selectedLines: any[] = this.spatialUtilService.getHighlightedLines();

        if (selectedLines.length > 2) {
            const validLineTypes = this.attributes.type.validLineTypes
                .split(',')
                .map((v: any) => parseInt(v))
                .filter((v: any) => !isNaN(v));

            if (selectedLines.every(selectedLine => selectedLine.attributes.type && validLineTypes.includes(selectedLine.attributes.type))) {
                return selectedLines;
            }

            console.debug(`${this.title} - lines are invalid`);
            tool.errors.push(tool.errorMessage2);
        } else if (selectedLines.length) {
            tool.errors.push(tool.errorMessage3);
        } else {
            tool.errors.push(tool.errorMessage1);
        }

        return undefined;
    }
}
